import { Link } from "@inertiajs/react";
import { useForm } from "@inertiajs/react";
import { Clock } from "lucide-react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import MinutesField from "@/components/forms/MinutesField";
import { formatMinutesDuration } from "@/lib/minutes";
import { BookOpen } from "lucide-react";

/**
 * The "log minutes for one student" form lives inline on the student
 * detail page (no standalone component). The screenshot below is the
 * same Card + form-field + Button primitives used everywhere else, so
 * visual updates to those primitives propagate here automatically.
 */
function SoloLogPreview() {
  const { data, setData } = useForm({ minutes: "15", note: "DEAR time, after lunch" });
  const minutesLabel = formatMinutesDuration(data.minutes) || "?";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          How long did Sarah read?
        </CardTitle>
        <CardDescription>Adds one log just for this student.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-5">
          <MinutesField name="minutes" required value={data.minutes} onChange={(e) => setData("minutes", e.target.value)} />
          <FormField name="note" label="Note (optional)" value={data.note} onChange={(e) => setData("note", e.target.value)} help="Shows on the student's reading list, where their family sees it too." />
          <Button type="submit" size="lg">
            <Clock className="h-4 w-4" />
            Log {minutesLabel}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function LoggingSoloMinutes() {
  return (
    <>
      <p>
        Sometimes you want to log for one student: silent reading time, a make-up session, or a student who read at recess on their own. Open that student's page and you'll find a quick form for it.
      </p>

      <h2>Where to find it</h2>
      <p>
        On your classroom dashboard, click the student's name in the leaderboard. Their page has a <strong>Log minutes</strong> form at the top.
      </p>

      <Screenshot caption="Logging for one student">
        <div className="max-w-md mx-auto">
          <SoloLogPreview />
        </div>
      </Screenshot>

      <p>
        It works like the whole-class form: enter hours, minutes or both, add a note if you'd like, and log. The note shows on the student's reading list, the same as for a class log.
      </p>

      <Callout variant="tip" title="When to use it">
        <p>
          If one student read a different amount from the rest of the class, uncheck them on the class form, log the rest together, then log them on their own. That's easier than fitting "everyone read 20 except Sarah, who read 30" into one log.
        </p>
      </Callout>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/logging-classroom-minutes">Logging the whole class at once</Link>.</li>
        <li><Link href="/help/articles/undoing-minute-batches">Undoing a reading log</Link>.</li>
      </ul>
    </>
  );
}
